import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { GoogleIcon } from "@/components/GoogleIcon";
import { analytics } from "@/lib/analytics";
import { useCaptcha } from "@/hooks/useCaptcha";

export default function SignIn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { captcha, captchaToken, captchaReady, resetCaptcha } = useCaptcha();

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken },
    });
    setLoading(false);
    resetCaptcha();
    if (error) {
      toast({ title: "Sign in failed", description: error.message, variant: "destructive" });
    } else {
      analytics.track("sign_in", { method: "email" });
      navigate(redirectTo || "/");
    }
  };

  const handleGoogle = async () => {
    analytics.track("sign_in", { method: "google" });
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: redirectTo ? `${window.location.origin}${redirectTo}` : window.location.origin },
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-black italic tracking-tight text-primary">SEND IT</CardTitle>
          <CardDescription>Sign in to your account</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {captcha}
            <Button type="submit" className="w-full font-semibold" disabled={loading || !captchaReady}>
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">or</span></div>
          </div>

          <Button variant="outline" className="w-full font-semibold" onClick={handleGoogle}>
            <GoogleIcon />
            Sign in with Google
          </Button>

          <div className="text-center text-sm space-y-1">
            <Link to="/forgot-password" className="text-primary hover:underline block">Forgot password?</Link>
            <p className="text-muted-foreground">
              Don't have an account?{" "}
              <Link to={redirectTo ? `/sign-up?redirect=${encodeURIComponent(redirectTo)}` : "/sign-up"} className="text-primary hover:underline">Create Account</Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
